export const CHAT_LIST_RECENCY_BUCKETS = [
  'today',
  'yesterday',
  'last7Days',
  'last30Days',
  'older',
] as const;

export type ChatListRecencyBucket = (typeof CHAT_LIST_RECENCY_BUCKETS)[number];

export type ChatListRecencyGroup<T extends { updatedAt: number }> = {
  bucket: ChatListRecencyBucket;
  chats: T[];
};

function startOfLocalDay(timestamp: number): Date {
  const date = new Date(timestamp);
  date.setHours(0, 0, 0, 0);
  return date;
}

function daysBefore(startOfToday: Date, days: number): number {
  const date = new Date(startOfToday);
  date.setDate(date.getDate() - days);
  return date.getTime();
}

function recencyBucketAt(
  now: number
): (timestamp: number) => ChatListRecencyBucket {
  const todayStart = startOfLocalDay(now).getTime();
  const yesterdayStart = daysBefore(startOfLocalDay(now), 1);
  // Inclusive 7- and 30-day calendar windows measured from today.
  const last7DaysStart = daysBefore(startOfLocalDay(now), 6);
  const last30DaysStart = daysBefore(startOfLocalDay(now), 29);

  return (timestamp) => {
    if (!Number.isFinite(timestamp) || timestamp < last30DaysStart) {
      return 'older';
    }
    if (timestamp >= todayStart) {
      return 'today';
    }
    if (timestamp >= yesterdayStart) {
      return 'yesterday';
    }
    if (timestamp >= last7DaysStart) {
      return 'last7Days';
    }
    return 'last30Days';
  };
}

/**
 * Sorts chats by last activity, then clusters them into recency sections.
 * Empty buckets are omitted so the list only renders headings that have chats.
 */
export function groupChatsByRecency<T extends { updatedAt: number }>(
  chats: readonly T[],
  now: number = Date.now()
): ChatListRecencyGroup<T>[] {
  const sorted = [...chats].sort((a, b) => b.updatedAt - a.updatedAt);
  const grouped = new Map<ChatListRecencyBucket, T[]>(
    CHAT_LIST_RECENCY_BUCKETS.map((bucket) => [bucket, []])
  );
  const bucketFor = recencyBucketAt(now);

  for (const chat of sorted) {
    grouped.get(bucketFor(chat.updatedAt))?.push(chat);
  }

  return CHAT_LIST_RECENCY_BUCKETS.flatMap((bucket) => {
    const bucketChats = grouped.get(bucket) ?? [];
    return bucketChats.length > 0 ? [{ bucket, chats: bucketChats }] : [];
  });
}
